/**
 * NotificationRow — a single row in the notification list (WI-56).
 *
 * Renders both score_tier_changed and new_camp event types.
 * Unread rows: brand-primary-lt background + green dot + bold school name.
 * Read rows: white background, normal weight, muted colors.
 *
 * Accessibility:
 *   - Entire row is a <button> with aria-label containing school name +
 *     event description + relative time (full sentence for screen readers).
 *   - role="listitem" set explicitly (overrides implicit button role).
 *   - Dot indicator is aria-hidden (decorative).
 *   - <time> element with datetime attribute for relative timestamps.
 */

import React from 'react';
import { relativeTime } from '../utils/dates';
import { formatCampDateRange } from '../utils/dates';
import type { NotificationItem } from '../api/notifications';

// ── Sport slug → display name mapping ────────────────────────────────────────
const SPORT_DISPLAY: Record<string, string> = {
  soccer_mens: "Men's Soccer",
  soccer_womens: "Women's Soccer",
  football_mens: 'Football',
};

function sportDisplayName(slug: string): string {
  return SPORT_DISPLAY[slug] ?? slug.replace(/_/g, ' ');
}

// ── Position slug → display label ─────────────────────────────────────────────
const POSITION_DISPLAY: Record<string, string> = {
  gk: 'GK',
  def: 'DEF',
  mid: 'MID',
  fwd: 'FWD',
  overall: 'Overall',
};

function positionDisplayName(slug: string): string {
  return POSITION_DISPLAY[slug] ?? slug.toUpperCase();
}

// ── Tier label (uppercase) ──────────────────────────────────────────────────
function tierLabel(tier: string): string {
  const map: Record<string, string> = {
    low: 'LOW',
    medium: 'MED',
    med: 'MED',
    high: 'HIGH',
  };
  return map[tier.toLowerCase()] ?? tier.toUpperCase();
}

/**
 * Format the event description line for a notification row.
 * Pure function — safe to call in tests without rendering.
 */
export function formatNotificationText(n: NotificationItem): string {
  if (n.event_type === 'score_tier_changed') {
    const sport = n.sport_slug ? sportDisplayName(n.sport_slug) : '';
    const position = n.position_slug ? ` ${positionDisplayName(n.position_slug)}` : '';
    const prefix = `${sport}${position}`;
    const from = n.old_tier ? tierLabel(n.old_tier) : '?';
    const to = n.new_tier ? tierLabel(n.new_tier) : '?';
    const year = n.target_year ? ` for ${n.target_year}` : '';
    return `${prefix} — opening likelihood changed from ${from} to ${to}${year}`;
  }

  if (n.event_type === 'new_camp') {
    const campName = n.camp_name ?? 'ID Camp';
    const dateStr = n.camp_start_date
      ? `, ${formatCampDateRange(n.camp_start_date, null)}`
      : '';
    return `New ID Camp: ${campName}${dateStr}`;
  }

  return '';
}

// ── Component ─────────────────────────────────────────────────────────────────

interface NotificationRowProps {
  notification: NotificationItem;
  onClick: (n: NotificationItem) => void;
}

export function NotificationRow({ notification: n, onClick }: NotificationRowProps) {
  const text = formatNotificationText(n);
  const timeStr = relativeTime(n.created_at);
  const opensExternally = n.event_type === 'new_camp' && !!n.camp_registration_url;
  const ariaLabel = opensExternally
    ? `${n.school_name}: ${text}, ${timeStr}, opens registration page in new tab`
    : `${n.school_name}: ${text}, ${timeStr}`;

  return (
    <button
      type="button"
      // role="listitem" applied explicitly — <button> has implicit role "button",
      // not "listitem", so we must override.
      role="listitem"
      onClick={() => onClick(n)}
      aria-label={ariaLabel}
      className={[
        'w-full text-left flex items-start gap-2 px-4 py-3',
        'border-b border-hairline',
        'motion-safe:transition-colors motion-safe:duration-150',
        'hover:opacity-90',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary',
        n.is_read ? 'bg-white' : 'bg-brand-primary-lt',
      ].join(' ')}
    >
      {/* Unread dot / spacer */}
      {!n.is_read ? (
        <span
          aria-hidden="true"
          className="w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0 mt-1.5"
        />
      ) : (
        <span className="w-1.5 flex-shrink-0" aria-hidden="true" />
      )}

      {/* Content */}
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        {/* School name + timestamp row */}
        <span className="flex items-baseline gap-2">
          <span
            className={`text-sm truncate ${
              n.is_read ? 'font-normal text-gray-700' : 'font-semibold text-gray-900'
            }`}
          >
            {n.school_name}
          </span>
          <time
            dateTime={n.created_at}
            className={`text-xs flex-shrink-0 ml-auto tabular-nums ${
              n.is_read ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            {timeStr}
          </time>
        </span>

        {/* Event description */}
        <span
          className={`text-xs leading-normal ${
            n.is_read ? 'text-gray-500' : 'text-gray-600'
          }`}
        >
          {text}
          {opensExternally && (
            <span
              aria-hidden="true"
              className="ml-1 inline-block text-gray-400"
              title="Opens in new tab"
            >
              ↗
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
